import { ComplianceCheck, GPCResult, ScanResult } from '../../utils/types';

export type GpcVerdictKind = 'honors' | 'substantially' | 'ignores' | 'inconclusive';

export interface GpcVerdict {
  kind: GpcVerdictKind;
  /** Short label rendered in the hero badge (replaces the score ring) */
  headline: string;
  /** One-sentence prose (replaces buildVerdict output) */
  detail: string;
}

/**
 * A GPC-only scan: gpcValidation ran and every other section was explicitly
 * skipped. Legacy reports with missing sections return false (optional
 * chaining → undefined !== 'skipped'), so they keep the full render.
 */
export function isGpcOnlyScan(result: ScanResult): boolean {
  const s = result.sections;
  if (!s?.gpcValidation) return false;
  return (
    s.gpcValidation.status !== 'skipped' &&
    s.uiCompliance?.status === 'skipped' &&
    s.trackerDetection?.status === 'skipped' &&
    s.privacyPolicy?.status === 'skipped'
  );
}

/**
 * True when the gpcValidation section's verdict cannot be trusted as a pass
 * or fail: the stage errored, the signal never went out, or the scanner
 * flagged the run as environment-suppressed. Shared by buildGpcVerdict
 * (hero ring/badge) and ReportHero's stage-chip rail so both surfaces agree
 * on tone by construction instead of duplicating this condition (#881).
 */
export function isGpcSectionInconclusive(result: ScanResult): boolean {
  const section = result.sections?.gpcValidation;
  const details = section?.details as GPCResult | undefined;
  return !section || section.status === 'error' || !details || details.signalSent === false || !!details.isInconclusive;
}

/**
 * gpcValidation checks about telling the visitor or the web that GPC is
 * honored, not about honoring it. Their failure never makes the verdict
 * "not honored" (#2064).
 */
const GPC_DISPLAY_CHECKS = new Set(['GPC Status Display', '.well-known GPC Declaration']);

export function buildGpcVerdict(result: ScanResult, hostname: string): GpcVerdict {
  const section = result.sections?.gpcValidation;
  const details = section?.details as GPCResult | undefined;

  // Inconclusive: checked first so a broken run never renders as a pass.
  if (isGpcSectionInconclusive(result)) {
    return {
      kind: 'inconclusive',
      // #1845: honored on the cold visit; trackers loaded only after a
      // generic Accept, whose weight as consent is unsettled.
      headline: details?.isInconclusive && details.signalHonoredBasis === 'unsettled-consent'
        ? 'GPC honored until Accept'
        : 'GPC test inconclusive',
      detail:
        details?.inconclusiveReason ||
        `We couldn't verify how ${hostname} responds to the Global Privacy Control signal. Try again, or run the full CCPA scan.`,
    };
  }

  // isGpcSectionInconclusive's false return guarantees section/details are
  // present (same underlying check) — TS can't see across the function call
  // boundary, so re-assert here rather than duplicating the condition inline.
  const definiteDetails = details as GPCResult;

  const failing = (section.checks || []).filter(
    (c: ComplianceCheck) => c.status === 'non_compliant' || c.status === 'error',
  );
  // #2064: only a processing failure may paint the hero "not honored". A
  // display or declaration check failing (GPC Status Display has been
  // non_compliant whatever the signal did since #1861) is a § 7025(c)(6)
  // issue, surfaced beneath an "honors" verdict. Unknown or unnamed checks
  // still count as processing failures, the safer reading.
  const processingFailing = failing.filter((c: ComplianceCheck) => !GPC_DISPLAY_CHECKS.has(c.name));

  // #836: the scanner's graduated middle verdict. The signal is not fully
  // honored, but the site suppressed the large majority of its trackers under
  // GPC and set no marketing cookies (a scored warning, not a critical
  // failure). Checked before the "ignores" branch so a substantially-honoring
  // site is not painted red — but only when nothing else genuinely failed.
  if (definiteDetails.signalSubstantiallyHonored && processingFailing.length === 0) {
    const trackerCount = definiteDetails.marketingDomainsLoaded?.length ?? 0;
    return {
      kind: 'substantially',
      headline: 'GPC signal substantially honored',
      detail:
        trackerCount > 0
          ? `${hostname} suppressed most marketing trackers after the Global Privacy Control opt-out signal, but ${trackerCount} tracker domain${trackerCount === 1 ? '' : 's'} still fired.`
          : `${hostname} substantially honored the Global Privacy Control opt-out signal, with a few residual trackers still firing.`,
    };
  }

  if (definiteDetails.signalHonored === false || processingFailing.length > 0) {
    const trackerCount = definiteDetails.marketingDomainsLoaded?.length ?? 0;
    return {
      kind: 'ignores',
      headline: 'GPC signal not honored',
      detail:
        trackerCount > 0
          ? `${hostname} kept loading ${trackerCount} marketing tracker domain${trackerCount === 1 ? '' : 's'} after receiving the Global Privacy Control opt-out signal.`
          : `${hostname} did not fully honor the Global Privacy Control opt-out signal in our test.`,
    };
  }

  const warnings = (section.checks || []).filter((c: ComplianceCheck) => c.status === 'warning').length;
  return {
    kind: 'honors',
    headline: 'GPC signal honored',
    detail:
      failing.length > 0
        ? `${hostname} honored the Global Privacy Control opt-out signal, with ${failing.length + warnings} issue${failing.length + warnings === 1 ? '' : 's'} worth reviewing.`
        : warnings > 0
          ? `${hostname} honored the Global Privacy Control opt-out signal, with ${warnings} minor issue${warnings === 1 ? '' : 's'} worth reviewing.`
          : `${hostname} honored the Global Privacy Control opt-out signal. Marketing trackers respected the opt-out in our test.`,
  };
}
